import {
  fetchCustomerRegionAsync,
  fetchCustomerSubRegionAsync,
} from "../../app/features/slices/customerRegionSlice";
import { fetchCustomerTypeAsync } from "../../app/features/slices/CustomerTypeSlice";
import {
  fetchProfileImage,
  fetchUser,
} from "../../app/features/thunks/customerThunks";
import { getUserGuideThunk } from "../../app/features/thunks/userGuideThunks";
import { Paths, UserRoles } from "../../constants/enums";

/**
 * Global app initialization — runs ONCE after login.
 *
 * Only fetches data that is needed across ALL pages:
 *  - Current user profile (header, nav, context)
 *  - Profile image (header avatar)
 *  - User guide (help button available everywhere)
 *
 * Page-specific data is fetched lazily by each page via usePageData hook.
 * This drastically reduces the initial API burst on login.
 */

const roleBasedFetchMap = {
  // All internal roles: user profile + user guide
  [UserRoles.ADMIN]: [
    { action: fetchUser, args: (user) => [user.id] },
    {
      action: fetchCustomerRegionAsync,
      shouldFetch: (state) =>
        state.customerregion.CustomerRegionData?.length === 0,
    },
    {
      action: fetchCustomerSubRegionAsync,
      shouldFetch: (state) =>
        state.customerregion.customerSubregiondata?.length === 0,
    },
    {
      action: fetchCustomerTypeAsync,
      shouldFetch: (state) => state.customerType.CustomerTypeData?.length === 0,
    },
    { action: getUserGuideThunk },
  ],

  [UserRoles.CUSTOMER]: [
    { action: fetchUser, args: (user) => [user.id] },
    { action: getUserGuideThunk },
  ],

  // External (onboarding form) — needs region + type for the form
  [UserRoles.EXTERNAL]: [
    {
      action: fetchCustomerRegionAsync,
      shouldFetch: (state) =>
        state.customerregion.CustomerRegionData?.length === 0,
    },
    {
      action: fetchCustomerSubRegionAsync,
      shouldFetch: (state) =>
        state.customerregion.customerSubregiondata?.length === 0,
    },
    {
      action: fetchCustomerTypeAsync,
      shouldFetch: (state) => state.customerType.CustomerTypeData?.length === 0,
    },
  ],
};

// Internal roles all share the same essential config
const internalEssentials = roleBasedFetchMap[UserRoles.ADMIN];
[
  UserRoles.SALES_MANAGER,
  UserRoles.REGIONAL_MANAGER,
  UserRoles.MARKETING_HEAD,
  UserRoles.CUSTOMER_SERVICE,
  UserRoles.VP_SALES,
  UserRoles.CFO,
  UserRoles.CEO,
  UserRoles.CCSUPERVISOR,
  UserRoles.LOGISTIC_MANAGER,
  UserRoles.LOGISTIC_SUPERVISOR,
  UserRoles.LOGISTIC_RESPONSIBLE,
  UserRoles.CC_MANAGER,
  UserRoles.MARKETING_MANAGER,
].forEach((role) => {
  roleBasedFetchMap[role] = internalEssentials;
});

// ─── Initialization entry point ─────────────────────────────────────────────

export const initializeApp = async (user, dispatch, getState) => {
  if (
    !user?.role &&
    ![Paths?.ADD_CUSTOMER, Paths?.UPDATE_CUSTOMER].includes(
      window.location.pathname
    )
  )
    return;

  const actions = roleBasedFetchMap[user?.role || UserRoles?.EXTERNAL] || [];
  if (actions.length === 0) return;

  try {
    const currentState = getState ? getState() : null;

    const fetchPromises = actions.map((actionConfig) => {
      if (currentState && actionConfig.shouldFetch) {
        if (!actionConfig.shouldFetch(currentState)) {
          return Promise.resolve();
        }
      }

      if (actionConfig.args) {
        const args = actionConfig.args(user);
        return dispatch(actionConfig.action(...args));
      }

      return dispatch(actionConfig.action());
    });

    // Fetch profile image for all roles
    if (user?.username) {
      dispatch(fetchProfileImage(user.username));
    }

    await Promise.allSettled(fetchPromises);
  } catch (error) {
    console.error("Error during app initialization:", error);
  }
};
